import { launch, handoff, mapCatalog } from './launcherProtocol'

const initial = () => ({ groups: [], status: 'idle', catalogError: null, launchError: null, pendingItemId: null, isLaunchPending: false, isViewAllPending: false })
// Owns async lifetime independently of rendering; original tuples stay in memory.
export function createLauncherController(runtime, manifest, publish, navigate) {
  let state = initial()
  let commands = new Map()
  let generation = 0
  let disposed = false
  let active = null
  let retryId = null
  let retryViewAll = false
  const update = patch => { state = { ...state, ...patch }; if (!disposed) publish(state) }
  const cancel = () => { generation++; if (active) active.abort(); active = null }
  const reset = () => { cancel(); commands.clear(); retryId = null; retryViewAll = false; update(initial()) }
  const unsubscribe = runtime.session.subscribe(reset)
  function operation() {
    cancel()
    const id = generation
    const snapshot = runtime.session.getSnapshot()
    const abort = new AbortController()
    active = abort
    return { signal: abort.signal, current: () => !disposed && id === generation && runtime.session.check() && runtime.session.getSnapshot() === snapshot }
  }
  function failure(error, op, field) {
    if (!op.current()) return
    if (error && error.status === 401) { runtime.session.invalidate(); return }
    update({ [field]: error && error.status === 409 ? 'La sesión o la selección cambió. Cerrá y volvé a abrir Mis aplicaciones.' :
      error && error.status === 403 ? 'Suite no autorizó esta aplicación o cuenta.' : 'No se pudo conectar con Suite. Intentá nuevamente.',
      ...(field === 'catalogError' ? { status: 'error' } : {}) })
  }
  async function refresh() {
    if (disposed || state.isLaunchPending || state.isViewAllPending || !runtime.session.check()) return
    const op = operation()
    commands.clear()
    retryId = null
    retryViewAll = false
    update({ ...initial(), status: 'loading' })
    try {
      const response = await runtime.client.get('/launcher/catalog', { signal: op.signal })
      if (!op.current()) return
      const mapped = mapCatalog(response.data, manifest)
      commands = mapped.commands
      update({ groups: mapped.groups, status: 'success' })
    } catch (error) { failure(error, op, 'catalogError') }
  }
  function requestLaunch(id, guard, viewAll = false) {
    if (disposed || state.isLaunchPending || state.isViewAllPending || !runtime.session.check() ||
      (viewAll ? !['success', 'error'].includes(state.status) : !commands.has(id))) return
    const command = commands.get(id)
    const op = operation()
    let started = false
    retryId = id
    retryViewAll = viewAll
    update({ isLaunchPending: !viewAll, isViewAllPending: viewAll, pendingItemId: viewAll ? null : id, launchError: null })
    const confirm = async () => {
      if (started || !op.current()) return
      started = true
      try {
        const response = await runtime.client.post(viewAll ? '/launcher/native-dashboard/handoff' : '/launcher/launch', viewAll ? {} : { ...command }, { signal: op.signal })
        if (!op.current()) return
        if (!(viewAll ? handoff(response.data) : launch(response.data, command.applicationCode))) throw new Error('Invalid launch')
        navigate(response.data.target)
      } catch (error) { failure(error, op, 'launchError') }
      finally { if (op.current()) update({ isLaunchPending: false, isViewAllPending: false, pendingItemId: null }) }
    }
    const decline = () => {
      if (started || !op.current()) return
      cancel()
      retryId = null
      retryViewAll = false
      update({ isLaunchPending: false, isViewAllPending: false, pendingItemId: null })
    }
    if (guard) guard({ onConfirm: confirm, onCancel: decline })
    else confirm()
  }
  return { refresh, requestLaunch, requestViewAll: guard => requestLaunch(null, guard, true), retry: guard => requestLaunch(retryId, guard, retryViewAll),
    close: reset, dispose() { disposed = true; cancel(); commands.clear(); unsubscribe() } }
}
